import { useEffect, useState, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FiSearch, FiFilter, FiFileText } from 'react-icons/fi';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import invoiceService from '../services/invoiceService';
import Button from '../components/Button';
import './Dashboard.css';
import '../assets/styles/ListTable.css';

const LIMIT_OPTIONS = [10, 20, 50, 100];

const Invoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [limitOption, setLimitOption] = useState('10');
  const [customLimit, setCustomLimit] = useState(25);
  const searchDebounce = useRef(null);

  const effectiveLimit = useMemo(() => {
    if (limitOption === 'custom') {
      const n = parseInt(customLimit, 10);
      return isNaN(n) ? 10 : Math.min(100, Math.max(1, n));
    }
    const n = parseInt(limitOption, 10);
    return isNaN(n) ? 10 : n;
  }, [limitOption, customLimit]);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setAppliedSearch(searchTerm), 400);
    return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedSearch, paymentStatus, startDate, endDate, effectiveLimit]);

  useEffect(() => {
    fetchInvoices();
  }, [currentPage, appliedSearch, paymentStatus, startDate, endDate, effectiveLimit]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: effectiveLimit };
      if (appliedSearch) params.search = appliedSearch;
      if (paymentStatus !== 'all') params.paymentStatus = paymentStatus;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await invoiceService.getAll(params);
      setInvoices(response.data || []);
      setTotal(response.total ?? 0);
      setPages(response.pages ?? 1);
    } catch (error) {
      toast.error('Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2 className="page-title">Invoices</h2>
          <p className="page-subtitle">Review and manage customer invoices</p>
        </div>
      </div>

      <div className="page-filters">
        <div className="search-box">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by invoice, customer, or ticket..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <FiFilter className="filter-icon" />
          <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} className="filter-select">
            <option value="all">All Status</option>
            <option value="unpaid">Unpaid</option>
            <option value="partial">Partially Paid</option>
            <option value="paid">Paid</option>
          </select>
        </div>
        <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="filter-select" />
        <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="filter-select" />
      </div>

      {invoices.length === 0 ? (
        <div className="empty-state">
          <FiFileText className="empty-icon" />
          <h3>No Invoices Found</h3>
          <p>Generate an invoice from a completed site visit</p>
        </div>
      ) : (
        <>
          <div className="data-table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Customer</th>
                  <th>Ticket</th>
                  <th>Date</th>
                  <th>Total</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice._id}>
                    <td><strong>{invoice.invoiceNumber || '—'}</strong></td>
                    <td>{invoice.customer?.name || invoice.customerName || '—'}</td>
                    <td>#{invoice.siteVisit?.ticketNumber || 'N/A'}</td>
                    <td>{invoice.invoiceDate ? format(new Date(invoice.invoiceDate), 'MMM dd, yyyy') : '—'}</td>
                    <td>Rs. {invoice.totalAmount?.toLocaleString() ?? 0}</td>
                    <td>
                      <span
                        className={`badge ${
                          invoice.paymentStatus === 'paid'
                            ? 'badge-success'
                            : invoice.paymentStatus === 'partial'
                            ? 'badge-warning'
                            : 'badge-error'
                        }`}
                      >
                        {invoice.paymentStatus?.toUpperCase() ?? '—'}
                      </span>
                    </td>
                    <td>
                      <div className="cell-actions">
                        <Link to={`/invoices/${invoice._id}`} className="action-btn action-btn-view">
                          View
                        </Link>
                        <Link to={`/invoices/${invoice._id}/edit`} className="action-btn action-btn-edit">
                          Edit
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(pages > 1 || total > invoices.length || total > 0) && (
            <div className="pagination-bar">
              <span className="pagination-info">
                Showing {(currentPage - 1) * effectiveLimit + 1}–{Math.min(currentPage * effectiveLimit, total)} of {total}
              </span>
              <div className="pagination-limit">
                <label className="pagination-limit-label">Per page</label>
                <select value={limitOption} onChange={(e) => setLimitOption(e.target.value)} className="pagination-limit-select">
                  {LIMIT_OPTIONS.map((n) => (<option key={n} value={String(n)}>{n}</option>))}
                  <option value="custom">Custom</option>
                </select>
                {limitOption === 'custom' && (
                  <input type="number" min={1} max={100} value={customLimit} onChange={(e) => setCustomLimit(parseInt(e.target.value, 10) || 10)} className="pagination-limit-input" />
                )}
              </div>
              <div className="pagination-controls">
                <button type="button" className="pagination-btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
                <span className="pagination-info" style={{ padding: '0 0.75rem' }}>Page {currentPage} of {pages}</span>
                <button type="button" className="pagination-btn" onClick={() => setCurrentPage((p) => Math.min(pages, p + 1))} disabled={currentPage === pages}>Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Invoices;
